import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Box, Button, Divider, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import useModal from '../../hooks/useModal';
import LineItemSpendModal from '../LineItems/LineItemSpendModal';
import AskAiButton from './AskAiButton';
import parseContractName from './contractName';
import { ackAttribution, adGroupHref, IssueRef, lineItemHref, resolutionText } from './issueRef';
import ruleLabel, { chipRedundant } from './ruleLabels';
import isIORule from './rules';
import { formatAge, formatAgo, SEVERITY_COLOR, SeverityDot, severitySoftBg } from './severity';

// Runs of 6+ digits (with optional thousands separators) inside evidence are almost
// always ids an operator needs to paste somewhere — surface them as copy tokens. The
// run must END on a digit so a list separator never lands inside a copied id.
const ID_RUN = /(\d[\d,]{4,}\d)/;

// One spacing token for every section, so the pane reads as a stack of equal units.
const SECTION = { px: 2, py: 1.5 } as const;

export interface EvidenceToken {
  isId: boolean;
  text: string;
}

export function evidenceTokens(evidence: string): EvidenceToken[] {
  return evidence
    .split(new RegExp(ID_RUN.source, 'g'))
    .filter((part) => part !== '')
    .map((part) => ({ isId: new RegExp(`^${ID_RUN.source}$`).test(part), text: part }));
}

interface IssueInspectorProps {
  item: IssueRef;
  onClose: () => void;
  onAck: (issueId: number) => void;
  onAskAi: (item: IssueRef) => void;
  askAiEnabled: boolean;
  acking: boolean;
  readOnly: boolean;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.disabled"
      sx={{ textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, display: 'block', mb: 0.5 }}
    >
      {children}
    </Typography>
  );
}

function TimelineCell({ label, value, lead = false }: { label: string; value: string; lead?: boolean }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, borderTop: '2px solid', borderColor: lead ? 'primary.main' : 'divider', pt: 0.75 }}>
      <SectionLabel>{label}</SectionLabel>
      <Typography variant="caption" color="text.secondary">
        {value}
      </Typography>
    </Box>
  );
}

// IssueInspector is the persistent detail pane. Its sections are fixed in order and
// evenly spaced so every issue reads the same way: context breadcrumb, issue heading,
// meta line (owner · IO · rule), evidence, a labelled Links list, the timeline, and a
// sticky action footer with the keyboard hint.
function IssueInspector({ item, onClose, onAck, onAskAi, askAiEnabled, acking, readOnly }: IssueInspectorProps) {
  const { issue, contract, ioName, insertionOrderId } = item;
  const { advertiser, rest } = parseContractName(contract.contract_name);
  const isIO = isIORule(issue.rule_id);
  const isAcknowledged = issue.state === 'acknowledged';
  const attribution = ackAttribution(issue);
  const chipLabel = ruleLabel(issue.rule_id);
  const showChip = chipLabel.trim() !== '' && !chipRedundant(chipLabel, issue.title);
  const campaign = issue.campaign_name || `campaign ${issue.campaign_id}`;
  const hasEvidence = issue.evidence.trim() !== '';

  const [showSpendModal, hideSpendModal] = useModal(
    () => (
      <LineItemSpendModal
        lineItemId={insertionOrderId}
        contractId={contract.contract_id}
        onClose={hideSpendModal}
      />
    ),
    { clickOutsideCloses: true },
    [insertionOrderId, contract.contract_id]
  );

  let status = 'Open — unacknowledged';
  if (readOnly || issue.state === 'resolved') {
    status = resolutionText(issue);
  } else if (isAcknowledged) {
    status = attribution ? `Acknowledged ${attribution}` : 'Acknowledged';
  }

  return (
    <Stack
      data-testid="issue-inspector"
      sx={{ height: '100%', minHeight: 0, bgcolor: 'background.paper' }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ px: 2, pt: 1.5 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              {advertiser || `Contract ${contract.contract_id}`}
            </Typography>
            {rest && (
              <Typography variant="caption" color="text.disabled">
                {`› ${rest}`}
              </Typography>
            )}
          </Stack>
          <IconButton size="small" aria-label="Close inspector" onClick={onClose} sx={{ mt: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 2, pt: 1 }}>
          <SeverityDot severity={issue.severity} size={10} />
          <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 650 }}>
            {issue.title}
          </Typography>
          {showChip && (
            <Box
              component="span"
              data-testid="inspector-type"
              sx={{
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.7,
                px: 0.75,
                borderRadius: 99,
                color: SEVERITY_COLOR[issue.severity],
                bgcolor: severitySoftBg(issue.severity),
              }}
            >
              {chipLabel}
            </Box>
          )}
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ px: 2, pt: 1, pb: 1.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PersonOutlineIcon sx={{ fontSize: 14 }} color="disabled" />
            <Typography variant="caption" color="text.secondary">
              {issue.trafficker?.name ?? 'Unassigned'}
            </Typography>
          </Stack>
          {issue.account_manager && (
            <Typography variant="caption" color="text.secondary" data-testid="inspector-account-manager">
              {`AM: ${issue.account_manager.name}`}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {`IO: ${ioName} #${insertionOrderId}`}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="baseline">
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}
            >
              Rule
            </Typography>
            <Tooltip title="Sentinel rule id (for debugging)">
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid={`alert-evidence-slug-${issue.id}`}
                sx={{ fontFamily: 'monospace' }}
              >
                {issue.rule_id}
              </Typography>
            </Tooltip>
          </Stack>
        </Stack>

        {hasEvidence && (
          <>
            <Divider />
            <Box sx={SECTION}>
              <SectionLabel>Evidence</SectionLabel>
              <Box
                data-testid={`alert-evidence-${issue.id}`}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {evidenceTokens(issue.evidence).map((token, index) =>
                  token.isId ? (
                    <Box
                      // Tokens are positional within one immutable string.
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      component="button"
                      type="button"
                      title="Click to copy"
                      onClick={() => navigator.clipboard?.writeText(token.text)}
                      sx={{
                        font: 'inherit',
                        px: 0.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 0.5,
                        bgcolor: 'background.paper',
                        cursor: 'pointer',
                      }}
                    >
                      {token.text}
                    </Box>
                  ) : (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>{token.text}</React.Fragment>
                  )
                )}
              </Box>
            </Box>
          </>
        )}

        <Divider />
        {/* One link per line, always in the same order — the block never reflows into a
            different shape from one issue to the next. */}
        <Box sx={SECTION} data-testid="inspector-links">
          <SectionLabel>Links</SectionLabel>
          <Stack spacing={0.5} alignItems="flex-start">
            {isIO && (
              <Link
                component={RouterLink}
                to={lineItemHref(item)}
                variant="caption"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="inspector-io-link"
              >
                {`Open ${ioName}`}
              </Link>
            )}
            <Link
              component={RouterLink}
              to={adGroupHref(item)}
              variant="caption"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="inspector-adgroup-link"
            >
              {isIO ? `via ${campaign}` : `Edit ${issue.campaign_name || `ad group ${issue.campaign_id}`}`}
            </Link>
            {isIO && (
              <Button
                variant="text"
                size="small"
                onClick={showSpendModal}
                data-testid="inspector-spend"
                sx={{ p: 0, minWidth: 0, textTransform: 'none', fontWeight: 400 }}
              >
                View daily spend
              </Button>
            )}
          </Stack>
        </Box>

        <Divider />
        <Stack direction="row" spacing={2} sx={SECTION} data-testid="inspector-timeline">
          <TimelineCell lead label="First seen" value={`${formatAge(issue.first_seen)} ago`} />
          <TimelineCell label="Last seen" value={formatAgo(issue.last_seen)} />
          <TimelineCell label="Status" value={status} />
        </Stack>
      </Box>

      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          px: 2,
          pt: 1.25,
          pb: 1.25,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {(!readOnly || askAiEnabled) && (
          <Stack direction="row" spacing={1}>
            {!readOnly && (
              <Button
                fullWidth
                variant={isAcknowledged ? 'outlined' : 'contained'}
                startIcon={isAcknowledged ? <CheckIcon /> : undefined}
                disabled={isAcknowledged || acking}
                onClick={() => onAck(issue.id)}
                data-testid="inspector-ack"
                sx={{ flex: 1, textTransform: 'none' }}
              >
                {isAcknowledged ? 'Acknowledged' : 'Ack'}
              </Button>
            )}
            {askAiEnabled && (
              <AskAiButton
                fullWidth
                label="Ask AI about this issue"
                onClick={() => onAskAi(item)}
                testId="inspector-ask-ai"
              />
            )}
          </Stack>
        )}
        <Typography
          variant="caption"
          color="text.disabled"
          data-testid="inspector-keyboard-hint"
          sx={{ display: 'block', mt: 1, textAlign: 'center' }}
        >
          {['j/k navigate', ...(readOnly ? [] : ['a ack']), ...(askAiEnabled ? ['i ask AI'] : []), 'Esc close'].join(
            ' · '
          )}
        </Typography>
      </Box>
    </Stack>
  );
}

export default IssueInspector;
